import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Autoplay, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import API_BASE_URL from '../config';
import { compressImage } from '../utils/imageUtils';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

import './Gallery.css';

interface GalleryImage {
  _id: string;
  imageUrl: string;
  caption?: string;
  size: number;
  createdAt: string;
}

interface StorageInfo {
  limit: number;
  usage: number;
}

// Background music for album mode (royalty-free sources)
const BACKGROUND_MUSIC = [
  { name: 'Romantic Piano', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3' },
  { name: 'Wedding Waltz', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3' },
  { name: 'Soft Memories', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3' },
];

const Gallery: React.FC = () => {
  const { accessToken, user } = useAuth();
  const history = useHistory();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isGuest = user?.role === 'guest';

  // Grid state
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [storage, setStorage] = useState<StorageInfo>({ limit: 100, usage: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [weddingId, setWeddingId] = useState('');

  // Upload modal state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Album mode state
  const [albumMode, setAlbumMode] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMusicIndex, setCurrentMusicIndex] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Auto-hide album controls
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (albumMode && showControls && isPlaying) {
      timeout = setTimeout(() => setShowControls(false), 3000);
    }
    return () => clearTimeout(timeout);
  }, [albumMode, showControls, isPlaying]);

  // Fetch images
  useEffect(() => {
    const fetchWeddingIdAndImages = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/wedding/current`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();

        if (res.ok && data.wedding) {
          const wId = data.wedding._id;
          setWeddingId(wId);

          const galleryRes = await fetch(`${API_BASE_URL}/api/gallery/${wId}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const galleryData = await galleryRes.json();

          if (galleryRes.ok) {
            setImages(galleryData.images);
            setStorage(galleryData.storage);
          }
        }
      } catch (err) {
        setError('Failed to load gallery');
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchWeddingIdAndImages();
    }
  }, [accessToken]);

  // ---- Upload handlers ----
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setShowUploadModal(true);
    setCaption('');
    setError('');
    setSuccess('');
  };

  const handleUpload = async () => {
    if (!selectedFile || !weddingId) return;
    setUploading(true);
    setError('');

    try {
      let fileToUpload = selectedFile;
      if (selectedFile.size > 5 * 1024 * 1024) {
        setSuccess('Compressing large image...');
        fileToUpload = await compressImage(selectedFile, 5);
      }

      const formData = new FormData();
      formData.append('image', fileToUpload);
      formData.append('weddingId', weddingId);
      formData.append('caption', caption);

      const response = await fetch(`${API_BASE_URL}/api/gallery/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        setImages([data.image, ...images]);
        setStorage({ ...storage, usage: data.storageUsage });
        setSuccess('Photo uploaded successfully!');
        closeModal();
      } else {
        setError(data.message || 'Upload failed');
      }
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setCaption('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id: string, size: number) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/gallery/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        setImages(images.filter(img => img._id !== id));
        setStorage({ ...storage, usage: Math.max(0, storage.usage - size) });
        setSuccess('Photo deleted');
      }
    } catch (err) {
      setError('Failed to delete photo');
    }
  };

  // ---- Album mode handlers ----
  const toggleMusic = async () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (err) {
          console.warn('Audio playback failed:', err);
          setIsPlaying(false);
        }
      }
    }
  };

  const nextTrack = async () => {
    const nextIndex = (currentMusicIndex + 1) % BACKGROUND_MUSIC.length;
    setCurrentMusicIndex(nextIndex);
    if (audioRef.current) {
      audioRef.current.src = BACKGROUND_MUSIC[nextIndex].url;
      if (isPlaying) {
        try {
          await audioRef.current.play();
        } catch (err) {
          console.warn('Next track playback failed:', err);
        }
      }
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const exitAlbumMode = () => {
    if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);
    setAlbumMode(false);
    if (isFullscreen && document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // ---- Computed ----
  const usageMB = (storage.usage / (1024 * 1024)).toFixed(1);
  const usagePercent = Math.min(100, (storage.usage / (storage.limit * 1024 * 1024)) * 100);

  if (loading) return <div className="admin-dashboard gallery-page-container loading">✨ Loading memories...</div>;

  // ============ ALBUM MODE ============
  if (albumMode) {
    if (images.length === 0) {
      return (
        <div className="digital-album empty-state">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="empty-content">
            <div className="empty-icon">📷</div>
            <h2>No Photos Yet</h2>
            <p>Add photos to the gallery to view them here</p>
            <button className="back-icon-btn" onClick={() => setAlbumMode(false)} title="Back to Gallery">
              <ArrowLeft size={22} strokeWidth={2.5} />
            </button>
          </motion.div>
        </div>
      );
    }

    return (
      <div className={`digital-album ${isFullscreen ? 'fullscreen' : ''}`} onClick={() => setShowControls(true)}>
        <audio ref={audioRef} src={BACKGROUND_MUSIC[currentMusicIndex].url} loop onEnded={nextTrack} />

        <Swiper
          modules={[EffectFade, Autoplay, Pagination]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          pagination={{ clickable: true, dynamicBullets: true }}
          loop={images.length > 1}
          onSlideChange={(swiper: SwiperType) => setCurrentSlide(swiper.realIndex)}
          className="album-swiper"
        >
          {images.map((image) => (
            <SwiperSlide key={image._id}>
              <motion.div className="slide-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                <div className="slide-image" style={{ backgroundImage: `url(${image.imageUrl})` }} />
                <AnimatePresence>
                  {image.caption && (
                    <motion.div className="slide-caption" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} transition={{ delay: 0.5 }}>
                      {image.caption}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="album-overlay" />

        <AnimatePresence>
          {showControls && (
            <motion.div className="album-controls" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <div className="controls-top">
                <button className="control-btn close-btn" onClick={exitAlbumMode}><span>✕</span></button>
                <div className="album-title">
                  <span className="title-icon">💒</span>
                  <span>Our Memories</span>
                </div>
                <button className="control-btn fullscreen-btn" onClick={toggleFullscreen}><span>⛶</span></button>
              </div>

              <div className="controls-bottom">
                <div className="music-controls">
                  <button className="music-btn play-pause" onClick={toggleMusic}>
                    <span className="music-icon">{isPlaying ? '⏸' : '▶'}</span>
                  </button>
                  <div className="music-info">
                    <span className="now-playing">Now Playing</span>
                    <span className="track-name">{BACKGROUND_MUSIC[currentMusicIndex].name}</span>
                  </div>
                  <button className="music-btn next-track" onClick={nextTrack}><span>⏭</span></button>
                </div>
                <div className="slide-counter">
                  <span>{currentSlide + 1}</span>
                  <span className="divider">/</span>
                  <span>{images.length}</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {isPlaying && (
          <div className="music-visualizer">
            <div className="bar" style={{ animationDelay: '0s' }} />
            <div className="bar" style={{ animationDelay: '0.2s' }} />
            <div className="bar" style={{ animationDelay: '0.4s' }} />
            <div className="bar" style={{ animationDelay: '0.1s' }} />
            <div className="bar" style={{ animationDelay: '0.3s' }} />
          </div>
        )}
      </div>
    );
  }

  // ============ GRID MODE (MAIN VIEW) ============
  return (
    <div className="admin-dashboard gallery-page-container">

      <header className="gallery-header">
        <div className="gallery-title-wrapper">
          <h1>Wedding Gallery</h1>
          <p>Capture and relive your most precious memories</p>
        </div>
      </header>

      {/* Album Mode Toggle */}
      <div className="album-mode-toggle-bar">
        <button className="album-mode-btn" onClick={() => { setAlbumMode(true); setShowControls(true); }}>
          🎬 Album Mode
        </button>
        {isGuest && (
          <button className="album-mode-btn secondary" onClick={() => history.goBack()}>
            ← Back
          </button>
        )}
      </div>

      {/* Storage — admin only */}
      {isAdmin && (
        <div className="storage-status">
          <div className="storage-info">
            <span>Storage Usage</span>
            <span>{usageMB} MB / {storage.limit} MB</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${usagePercent}%`, backgroundColor: usagePercent > 90 ? '#ff5252' : '#6c5ce7' }}></div>
          </div>
          {usagePercent > 90 && <p className="storage-warning">⚠️ Running low on space! Contact SuperAdmin to increase.</p>}
        </div>
      )}

      {/* Upload actions — admin only */}
      {isAdmin && (
        <div className="upload-actions">
          <input type="file" accept="image/*" onChange={handleFileSelect} ref={fileInputRef} style={{ display: 'none' }} />
          <button className="upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? '⌛ Uploading...' : '📸 Upload Photo'}
          </button>
          <button className="capture-btn" onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.setAttribute('capture', 'environment');
              fileInputRef.current.click();
              fileInputRef.current.removeAttribute('capture');
            }
          }} disabled={uploading}>
            🤳 Take Photo
          </button>
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="upload-modal">
            <div className="modal-header">
              <h2>Add a Caption</h2>
              <button className="close-modal-btn" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-content">
              <div className="preview-container">
                <img src={previewUrl} alt="Preview" />
              </div>
              <div className="caption-input-container">
                <textarea
                  placeholder="Write something about this photo... (optional)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value.slice(0, 100))}
                  maxLength={100}
                ></textarea>
                <div className="char-count">{caption.length}/100</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeModal} disabled={uploading}>Cancel</button>
              <button className="confirm-upload-btn" onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Grid */}
      <div className="image-grid">
        {images.length === 0 ? (
          <div className="empty-gallery">
            <div className="empty-icon">📸</div>
            <h3>No memories captured yet</h3>
            <p>Your beautiful wedding journey starts here. Start uploading photos to create your gallery!</p>
          </div>
        ) : (
          images.map((img) => (
            <div key={img._id} className="image-card">
              <img src={img.imageUrl} alt={img.caption || 'Gallery'} loading="lazy" />
              {img.caption && (
                <div className="image-caption-tag">
                  {img.caption}
                </div>
              )}
              {isAdmin && (
                <div className="image-overlay">
                  <button className="delete-photo-btn" onClick={() => handleDelete(img._id, img.size)}>🗑️</button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Gallery;
